import React, { useContext } from "react";
import Select, { SelectOption } from "@amzn/meridian/select";
import Row from "@amzn/meridian/row";
import AuditContext from "../../store/audit-store";
import { PlanGroupSummary } from "../../admin-api/generated-src";
import { COMPLETED_STATUS, IN_PROGRESS_STATUS, OVERALL_SEGMENT_ID, SCHEDULED_STATUS } from "../../constants";

/**
 * Date + Shift pickers for CH (plan-group) nodes, mirroring the legacy RosterSelect UX so both
 * node types share the same two-dropdown entry experience. Unlike legacy, a selection here resolves
 * to a plan group (whose segments render as tabs beneath) rather than to a single roster.
 *
 * The plan-group list is already loaded into AuditContext; this component only derives the two
 * dropdowns from it and updates selectedPlanGroupId. Segment-tab loading is handled downstream.
 */
/**
 * Distinct shift dates across the plan groups, sorted chronologically. The API does not guarantee
 * plan-group order; ISO-8601 (YYYY-MM-DD) dates sort lexicographically into chronological order.
 */
export function chronologicalDates(planGroups: PlanGroupSummary[]): string[] {
  return Array.from(
    new Set(planGroups.map((pg) => pg.shiftDate).filter((d): d is string => !!d))
  ).sort();
}

interface ShiftOption {
  planGroupId: string;
  label: string;
}

/** Dropdown label for a plan group: shift type plus the version when present (e.g. "MOR [v2]"). */
export function shiftLabel(planGroup: PlanGroupSummary): string {
  return planGroup.version != null
    ? `${planGroup.shiftType ?? ""} [v${planGroup.version}]`
    : `${planGroup.shiftType ?? ""}`;
}

/**
 * One Shift option per plan group for the given date — every version is individually selectable
 * (no latest-version collapse), matching legacy. Sorted by shift type then version so versions of
 * one shift read in order. The option value is the planGroupId, which uniquely identifies a version.
 */
export function shiftOptionsForDate(planGroups: PlanGroupSummary[], date?: string): ShiftOption[] {
  return planGroups
    .filter((pg) => pg.shiftDate === date && !!pg.planGroupId)
    .sort((a, b) =>
      (a.shiftType ?? "").localeCompare(b.shiftType ?? "") || (a.version ?? 0) - (b.version ?? 0)
    )
    .map((pg) => ({ planGroupId: pg.planGroupId as string, label: shiftLabel(pg) }));
}

/**
 * Plan group to auto-select when the audit page is first opened, by operational priority:
 *   1. the In-Progress plan group (the shift happening now);
 *   2. otherwise the nearest upcoming Scheduled plan group (smallest startTime >= now);
 *   3. otherwise the most recent past Completed plan group (largest startTime < now).
 *
 * Status is the server-computed PlanGroupSummary.status; startTime is an ISO-8601 offset datetime
 * (e.g. "2026-07-20T20:30:00-05:00"), compared as an absolute instant via Date.parse. Overridden
 * (superseded) versions are excluded — the caller resolves the chosen shift to its current version
 * via defaultPlanGroupIdForDate. Entries without a planGroupId or a parseable startTime are ignored.
 * Returns undefined when nothing matches, so the caller can fall back to its default.
 */
export function defaultPlanGroupOnVisit(
  planGroups: PlanGroupSummary[],
  now: number = Date.now()
): PlanGroupSummary | undefined {
  const startMs = (pg: PlanGroupSummary): number => Date.parse(pg.startTime ?? "");
  const usable = planGroups.filter((pg) => !!pg.planGroupId && !Number.isNaN(startMs(pg)));

  const inProgress = usable
    .filter((pg) => pg.status === IN_PROGRESS_STATUS)
    .sort((a, b) => startMs(a) - startMs(b));
  if (inProgress.length > 0) {
    return inProgress[0];
  }

  const upcomingScheduled = usable
    .filter((pg) => pg.status === SCHEDULED_STATUS && startMs(pg) >= now)
    .sort((a, b) => startMs(a) - startMs(b));
  if (upcomingScheduled.length > 0) {
    return upcomingScheduled[0];
  }

  const pastCompleted = usable
    .filter((pg) => pg.status === COMPLETED_STATUS && startMs(pg) < now)
    .sort((a, b) => startMs(b) - startMs(a));
  if (pastCompleted.length > 0) {
    return pastCompleted[0];
  }

  return undefined;
}

/**
 * Default plan group to select for a date: the highest version of the preferred shift type when it
 * exists for that date, otherwise the highest version of the first shift type. Returns undefined
 * when the date has no plan groups.
 */
export function defaultPlanGroupIdForDate(
  planGroups: PlanGroupSummary[],
  date?: string,
  preferredShiftType?: string
): string | undefined {
  const forDate = planGroups.filter((pg) => pg.shiftDate === date && !!pg.planGroupId);
  if (forDate.length === 0) {
    return undefined;
  }
  const shiftType = forDate.some((pg) => pg.shiftType === preferredShiftType)
    ? preferredShiftType
    : [...forDate].sort((a, b) => (a.shiftType ?? "").localeCompare(b.shiftType ?? ""))[0].shiftType;
  return forDate
    .filter((pg) => pg.shiftType === shiftType)
    .reduce((current, pg) => ((pg.version ?? 0) >= (current.version ?? 0) ? pg : current))
    .planGroupId;
}

export default function PlanGroupSelect() {
  const { planGroups, selectedPlanGroupId, setSelectedPlanGroupId, setSelectedSegmentId } =
    useContext(AuditContext);

  const selectedPlanGroup: PlanGroupSummary | undefined = planGroups.find(
    (pg) => pg.planGroupId === selectedPlanGroupId
  );
  const selectedDate = selectedPlanGroup?.shiftDate;
  const selectedShift = selectedPlanGroup?.shiftType;

  const dateOptions: string[] = chronologicalDates(planGroups);

  const shiftOptions: ShiftOption[] = shiftOptionsForDate(planGroups, selectedDate);

  const onDateChange = (date: string) => {
    // Re-anchor to a concrete plan group on the new date (preferring the current shift type), since
    // shift options are now versioned plan groups, not bare shift types.
    setSelectedPlanGroupId(defaultPlanGroupIdForDate(planGroups, date, selectedShift));
    // A new plan group means the prior segment selection no longer applies — reset to Overall.
    setSelectedSegmentId(OVERALL_SEGMENT_ID);
  };

  // A shift option's value is the planGroupId, so the selection is the chosen version directly.
  const onShiftChange = (planGroupId: string) => {
    setSelectedPlanGroupId(planGroupId);
    setSelectedSegmentId(OVERALL_SEGMENT_ID);
  };

  return (
    <Row>
      <Select
        value={selectedDate}
        onChange={onDateChange}
        placeholder="Select Date"
        width={180}
      >
        {dateOptions.map((date) => (
          <SelectOption key={`${date}-date`} value={date} label={date} />
        ))}
      </Select>
      <Select
        value={selectedPlanGroupId}
        onChange={onShiftChange}
        placeholder="Select Shift"
        width={180}
      >
        {shiftOptions.map((option) => (
          <SelectOption key={option.planGroupId} value={option.planGroupId} label={option.label} />
        ))}
      </Select>
    </Row>
  );
}
